import React, { useReducer, useContext } from 'react';
import api from '../../utils/api';
import NotificationContext from './notificationContext';
import notificationReducer from './notificationReducer';
import AuthContext from '../auth/authContext';
import {
  GET_NOTIFICATIONS,
  GET_UNREAD_COUNT,
  MARK_NOTIFICATIONS_READ,
  DELETE_NOTIFICATIONS,
  NOTIFICATION_ERROR,
  CLEAR_NOTIFICATIONS,
  CLEAR_ERRORS
} from '../types';

const NotificationState = props => {
  const initialState = {
    notifications: [],
    unreadCount: 0,
    error: null,
    loading: true
  };

  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user } = useContext(AuthContext);

  // Get all notifications
  const getNotifications = async () => {
    // Only allow authenticated users to fetch notifications
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: GET_NOTIFICATIONS,
        payload: []
      });
      return;
    }

    try {
      const res = await api.get('/api/notifications');

      dispatch({
        type: GET_NOTIFICATIONS,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: NOTIFICATION_ERROR,
        payload: err.response?.data?.message || 'Error fetching notifications'
      });
    }
  };

  // Get unread notification count
  const getUnreadCount = async () => {
    // Only allow authenticated users to fetch unread count
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: GET_UNREAD_COUNT,
        payload: 0
      });
      return;
    }

    try {
      const res = await api.get('/api/notifications/unread-count');

      dispatch({
        type: GET_UNREAD_COUNT,
        payload: res.data.unreadCount
      });
    } catch (err) {
      dispatch({
        type: NOTIFICATION_ERROR,
        payload: err.response?.data?.message || 'Error fetching unread count'
      });
    }
  };

  // Mark notifications as read
  const markNotificationsRead = async (notificationIds) => {
    // Only allow authenticated users to mark notifications as read
    if (!user || !localStorage.getItem('token')) {
      return;
    }

    try {
      await api.put('/api/notifications/mark-read', { notificationIds });

      dispatch({
        type: MARK_NOTIFICATIONS_READ,
        payload: notificationIds
      });

      // Update unread count
      getUnreadCount();
    } catch (err) {
      dispatch({
        type: NOTIFICATION_ERROR,
        payload: err.response?.data?.message || 'Error marking notifications as read'
      });
    }
  };

  // Delete notifications
  const deleteNotifications = async (notificationIds) => {
    // Only allow authenticated users to delete notifications
    if (!user || !localStorage.getItem('token')) {
      return false;
    }

    try {
      console.log('Sending delete request with IDs:', notificationIds);
      
      const response = await api.delete('/api/notifications', { 
        data: { notificationIds } 
      });
      
      console.log('Delete response:', response);

      dispatch({
        type: DELETE_NOTIFICATIONS,
        payload: notificationIds
      });

      // Update unread count
      getUnreadCount();
      
      return true;
    } catch (err) {
      console.error('Error deleting notifications:', err);
      
      dispatch({
        type: NOTIFICATION_ERROR,
        payload: err.response?.data?.message || 'Error deleting notifications'
      });
      
      throw err; // Re-throw to allow component to handle error
    }
  };

  // Clear notifications
  const clearNotifications = () => {
    dispatch({ type: CLEAR_NOTIFICATIONS });
  };

  // Clear errors
  const clearErrors = () => {
    dispatch({ type: CLEAR_ERRORS });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        error: state.error,
        loading: state.loading,
        getNotifications,
        getUnreadCount,
        markNotificationsRead,
        deleteNotifications,
        clearNotifications,
        clearErrors
      }}
    >
      {props.children}
    </NotificationContext.Provider>
  );
};

export default NotificationState;